import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

import useFetch from "../../hooks/useFetch";
import useDeviceType from "../../hooks/useDeviceType";
import { useFilters } from "../../hooks/useFilters";

import { applyDiscountRules, capitalizeWords } from "../../helpers/common";
import { ReactComponent as Close } from "../../SVGs/Close.svg";
import Card from "../../components/Card/Card";
import LoaderOverlay from "../../components/LoaderOverlay/LoaderOverlay";

import "./Search.scss";

const Search = () => {
    const slug = useParams().slug;
    const pageType = "search";
    const deviceType = useDeviceType();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const discountRules = useSelector((state) => state.discountRules);

    // Filters
    const { filters, areFiltersLoading, areFiltersError, setAreFiltersError, retryFilters } = useFilters();
    const [selectedFilters, setSelectedFilters] = useState(new Map());
    const [expandedFilters, setExpandedFilters] = useState({});

    // Pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);

    // Product
    const [productsList, setProductsList] = useState([]);
    const [isProductsLoading, setIsProductsLoading] = useState(false);
    const [isProductsError, setIsProductsError] = useState(false);
    const [productsRetries, setProductsRetries] = useState(0);
    const [isFiltersVisible, setIsFiltersVisible] = useState(false);

    const [isNewPageLoading, setIsNewPageLoading] = useState(false);

    const [titleParam, setTitleParam] = useState("");

    const toggleFiltersVisibility = () => {
        setIsFiltersVisible((prevState) => !prevState);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    let productsBaseUrl = "/products";
    let productsParams = new URLSearchParams({
        populate: "deep,3",
        "pagination[page]": page,
        "pagination[pageSize]": pageSize,
        "filters[Status][$eq]": true,
        sort: "CreationDate:desc",
        locale: "en",
    });
    if (productsRetries > 0) productsParams.set("_retry", productsRetries);
    if (slug == "best-sellers") productsParams.set("populate", "deep,4");

    let filterKey = "filters[$or][0][Title][$contains]";
    let secondFilterKey = "filters[$or][1][localizations][Title][$contains]";

    if (slug == "new") {
        productsParams.set("sort", "CreationDate:desc");
    } else if (slug != "best-sellers") {
        productsParams.set(filterKey, slug);
        if (pageType == "search") {
            productsParams.set(secondFilterKey, slug);
        }
    }

    selectedFilters.forEach((ids, filter) => {
        let index = 0;
        switch (filter) {
            case "Vendor":
                ids.forEach((id) => {
                    if (slug == "best-sellers") productsParams.append(`filters[Product][vendor][id][$in][${index}]`, id);
                    else productsParams.append(`filters[vendor][id][$in][${index}]`, id);
                    index++;
                });
                break;
            case "Color":
                ids.forEach((id) => {
                    if (slug == "best-sellers") productsParams.append(
                        `filters[Product][Variants][Color][id][$in][${index}]`,
                        id
                    );
                    else productsParams.append(
                        `filters[Variants][Color][id][$in][${index}]`,
                        id
                    );
                    index++;
                });
                break;
            case "Size":
                ids.forEach((id) => {
                    if (slug == "best-sellers") productsParams.append(
                        `filters[Product][Variants][Sizes][Size][id][$in][${index}]`,
                        id
                    );
                    else productsParams.append(
                        `filters[Variants][Sizes][Size][id][$in][${index}]`,
                        id
                    );
                    index++;
                });
                break;
            case "Price":
                ids.forEach((id) => {
                    if (slug == "best-sellers") productsParams.append(`filters[Product][Price][$lt]`, id);
                    else productsParams.append(`filters[Price][$lt]`, id);
                    index++;
                });
                break;
            default:
                break;
        }
    });

    const productsUrl = `${productsBaseUrl}?${productsParams.toString()}`;
    const {
        data: productsData,
        loading: productsLoading,
        error: productsError,
        meta: productsMeta,
    } = useFetch(productsUrl, true);

    const toggleShowAll = (index) => {
        setExpandedFilters((prev) => {
            return {
                ...prev,
                [index]: !prev[index],
            };
        });
    };

    const handleFilterChange = (event) => {
        const [id, filter] = event.target.id.split("-");
        const isChecked = event.target.checked;

        setSelectedFilters((prev) => {
            const newFilters = new Map(prev);

            if (isChecked) {
                if (!newFilters.has(filter)) {
                    newFilters.set(filter, new Set());
                }
                newFilters.get(filter).add(id);
            } else {
                if (newFilters.has(filter)) {
                    newFilters.get(filter).delete(id);
                    if (newFilters.get(filter).size === 0) {
                        newFilters.delete(filter);
                    }
                }
            }
            return newFilters;
        });

        setProductsList([]);
        setPage(1);
    };

    const getName = (color) => {
        let colorName = color?.attributes?.Name;
        if (currentLanguage != "en") {
            const localization = color?.attributes?.localizations?.data?.find(
                (local) => local?.attributes?.locale === currentLanguage
            );
            if (localization) {
                colorName = localization?.attributes?.Name;
            }
        }

        return colorName;
    };

    useEffect(() => {
        setProductsList([]);
        setPage(1);
    }, [slug, pageType]);

    useEffect(() => {
        if (!productsLoading && productsData) {
            let prods = []
            if (slug == "best-sellers") {
                prods = productsData?.map((prod) => prod?.attributes?.Product?.data)
            }
            const discountedProductsData = applyDiscountRules(
                slug == "best-sellers" ? prods : [...productsData],
                discountRules
            );
            setProductsList((prevProducts) =>
                page === 1
                    ? [...discountedProductsData]
                    : [...prevProducts, ...discountedProductsData]
            );
            setIsProductsLoading(false);
            setIsNewPageLoading(false);
        } else if (productsLoading && !productsError) {
            setIsProductsLoading(true);
        } else if (!productsLoading && productsError && !productsData) {
            setIsProductsLoading(false);
            setIsProductsError(true);
        }
    }, [productsLoading]);

    useEffect(() => {
        if (slug != "new" && slug != "best-sellers") {
            if (pageType == "search") {
                document.title = `${t("general.classy-ym")} - ${t(
                    "category.search-for"
                )} ${capitalizeWords(slug)}`;
            }
        } else {
            if (slug == "new") {
                document.title = `${t("general.classy-ym")} - ${t("home.new-in")}`;
            } else if (slug == "best-sellers") {
                document.title = `${t("general.classy-ym")} - ${t(
                    "home.best-sellers"
                )}`;
            }
        }

        setTitleParam(slug);
    }, [slug]);

    return (
        <>
            <Helmet>
                <meta name="description" content={t("home.default-description")} />
                <meta name="keywords" content={t("home.default-keywords")} />
            </Helmet>
            <section className="search-page">
                <LoaderOverlay />
                <div className="search-content">
                    <h1 className="search-title">
                        {slug == "new" || slug == "best-sellers" ? (
                            <>
                                {slug == "new" && t("category.new")}
                                {slug == "best-sellers" && t("home.best-sellers")}
                            </>
                        ) : (
                            <>
                                {pageType == "search" && t("category.search-results-for")}{" "}{titleParam}
                            </>
                        )}
                    </h1>
                    <button
                        className="filters-toggle-btn"
                        onClick={toggleFiltersVisibility}
                    >
                        <p>{t("category.filters")}</p> <img src="/img/filter.svg" />
                    </button>

                    <div className="container">
                        <div
                            className={`filters ${deviceType == "mobile" ? isFiltersVisible ? "visible" : "hidden" : ""}`}
                        >
                            {areFiltersLoading && !isProductsLoading ? (
                                <LoaderOverlay
                                    loadingState={areFiltersLoading}
                                    errorState={areFiltersError}
                                    setErrorState={setAreFiltersError}
                                    retryFunc={retryFilters}
                                />
                            ) : (
                                <></>
                            )}
                            {filters.map((filter, index) => {
                                const isExpanded = expandedFilters[index] || false;
                                return (
                                    <div className="filter-group" key={index}>
                                        <h2 className="title">{filter.title}</h2>
                                        <div className="values">
                                            {(isExpanded
                                                ? filter.values
                                                : filter.values.slice(0, 5)
                                            ).map((value) => {
                                                return (
                                                    <div className="filter" key={value.id}>
                                                        <input
                                                            className="filter-checkbox"
                                                            type="checkbox"
                                                            name={value?.attributes?.Name}
                                                            id={`${value.id}-${filter.title}`}
                                                            onChange={handleFilterChange}
                                                        />
                                                        <label
                                                            className="filter-title"
                                                            htmlFor={`${value.id}-${filter.title}`}
                                                        >
                                                            {getName(value)}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                            {filter.values.length > 5 ? (
                                                <div
                                                    className="show-more-filters"
                                                    onClick={() => toggleShowAll(index)}
                                                >
                                                    {isExpanded
                                                        ? t("category.show-less")
                                                        : `${t("category.show-all")} (${filter.values.length
                                                        })`}
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <Close className="search-btn" onClick={toggleFiltersVisibility} />
                        </div>
                        <div className="products">
                            {isProductsLoading &&
                                !areFiltersLoading &&
                                !isNewPageLoading ? (
                                <LoaderOverlay
                                    loadingState={isProductsLoading}
                                    errorState={isProductsError}
                                    setErrorState={setIsProductsError}
                                    retryFunc={() => setProductsRetries((prev) => prev + 1)}
                                />
                            ) : (
                                <></>
                            )}
                            <div className="products-container">
                                {productsList.length === 0 ? (
                                    <span className="empty-list">
                                        {t("home.no-products-available")}
                                    </span>
                                ) : (
                                    <></>
                                )}
                                {productsList.map((product) => {
                                    return (
                                        <Card
                                            areDiscountRulesApplied={true}
                                            product={product}
                                            isCategory={true}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Search;

/*
    FIX SEARCH BUG: 25 FEB, 2026
    DESCRIPTION: 
    1. Separated Search and Category into standalone pages to eliminate state conflicts.
*/